import { FieldOptionBase } from 'module/api/field/types';
import { byLabel } from 'module/helpers/comparators/by-label';
import { ClassNameOnlyProps } from 'module/types/common';
import { MultiUserField } from 'module/types/filter-field/field/filterable-field';
import { VirtualizedMenuList } from 'module/ui/components/atoms/select/virtualized-menu-list';
import { FC, ReactNode, RefObject, useCallback, useMemo } from 'react';
import StateManager from 'react-select';
import styled from 'styled-components';
import { Popover, Select } from '../components/components';
import { UserFieldOptionsGetter } from '../types';

type Props = ClassNameOnlyProps & {
  field: MultiUserField;
  title: ReactNode;
  optionsGetter: UserFieldOptionsGetter;
  onChange: (latest: number[]) => void;
  selectRef: RefObject<StateManager>;
};

const StyledPopover = styled(Popover)`
  &&& {
    height: 400px;
  }
`;

export const ControlledMultiUserFieldEditorPopover: FC<
  React.PropsWithChildren<Props>
> = ({ field, title, onChange, optionsGetter, selectRef, className }) => {
  const options = useMemo(() => optionsGetter(field), [field, optionsGetter]);
  const onValueChange = useCallback(
    (latest: FieldOptionBase[]) => {
      return onChange?.((latest ?? []).sort(byLabel).map((x) => x.value));
    },
    [onChange],
  );

  const usersInOptions = useMemo(
    () => options.flatMap((option) => option.options),
    [options],
  );

  const selectedUsers = useMemo(
    () =>
      (field.value ?? [])
        .map((id) => usersInOptions?.find((u) => u?.value === id))
        .filter(Boolean),
    [field.value, usersInOptions],
  );

  return (
    <StyledPopover className={className}>
      <Popover.Title>{title}</Popover.Title>
      <Select
        ref={selectRef}
        type="user"
        isMulti
        components={{ MenuList: VirtualizedMenuList }}
        options={options}
        value={selectedUsers}
        onChange={onValueChange}
        grouped
      />
    </StyledPopover>
  );
};
